<script setup>
import { computed, ref } from 'vue'
import dayjs from 'dayjs'


const props = defineProps({
  modelValue: {
    type: Object,
    default: null,
  },
  title: {
    type: String,
    required: true,
  },
  icon: {
    type: String,
    required: true,
  },
  showConfirmed: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['save', 'delete', 'cancel'])


const DATE_MASK = 'YYYY/MM/DD'
const TIME_MASK = 'HH:mm'

const form = ref({ startDate: '', startTime: '', endDate: '', endTime: '', confirmed: false })

const onShow = () => {
  const start = dayjs(props.modelValue.start)
  const end = dayjs(props.modelValue.end)
  form.value = {
    startDate: start.format(DATE_MASK),
    startTime: start.format(TIME_MASK),
    endDate: end.format(DATE_MASK),
    endTime: end.format(TIME_MASK),
    confirmed: props.modelValue.confirmed,
  }
}

const toDate = (date, time) => dayjs(`${date.replaceAll('/', '-')}T${time}`)
const start = computed(() => toDate(form.value.startDate, form.value.startTime))
const end = computed(() => toDate(form.value.endDate, form.value.endTime))
const isValid = computed(() => start.value.isValid() && end.value.isValid() && end.value.isAfter(start.value))

const onSave = () => emit('save', {
  ...props.modelValue,
  start: start.value.toISOString(),
  end: end.value.toISOString(),
  confirmed: form.value.confirmed,
})
</script>

<template>
  <q-dialog
    :model-value="props.modelValue !== null"
    class="dialog-xs"
    @show="onShow"
    @hide="emit('cancel')"
  >
    <q-card>
      <q-card-section class="row items-center q-gutter-sm">
        <q-avatar
          :icon="props.icon"
          color="primary"
          text-color="white"
          size="md"
        />
        <span class="text-h6">{{ props.title }}</span>
      </q-card-section>

      <q-card-section class="q-pt-none">
        <div class="row q-col-gutter-sm">
          <div class="col-12 col-sm-6">
            <q-input v-model="form.startDate" mask="date" :label="$t('common.start')">
              <template #append>
                <q-icon name="mdi-calendar" class="cursor-pointer">
                  <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                    <q-date v-model="form.startDate" :mask="DATE_MASK" />
                  </q-popup-proxy>
                </q-icon>
              </template>
            </q-input>
          </div>
          <div class="col-12 col-sm-6">
            <q-input v-model="form.startTime" mask="time" :label="$t('common.start')">
              <template #append>
                <q-icon name="mdi-clock-outline" class="cursor-pointer">
                  <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                    <q-time v-model="form.startTime" format24h />
                  </q-popup-proxy>
                </q-icon>
              </template>
            </q-input>
          </div>
          <div class="col-12 col-sm-6">
            <q-input v-model="form.endDate" mask="date" :label="$t('common.end')">
              <template #append>
                <q-icon name="mdi-calendar" class="cursor-pointer">
                  <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                    <q-date v-model="form.endDate" :mask="DATE_MASK" />
                  </q-popup-proxy>
                </q-icon>
              </template>
            </q-input>
          </div>
          <div class="col-12 col-sm-6">
            <q-input v-model="form.endTime" mask="time" :label="$t('common.end')">
              <template #append>
                <q-icon name="mdi-clock-outline" class="cursor-pointer">
                  <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                    <q-time v-model="form.endTime" format24h />
                  </q-popup-proxy>
                </q-icon>
              </template>
            </q-input>
          </div>
          <div v-if="props.showConfirmed" class="col-12">
            <q-toggle v-model="form.confirmed" :label="$t('common.confirmed')" color="positive" />
          </div>
        </div>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn
          v-if="props.modelValue?.id"
          flat
          color="negative"
          icon="mdi-delete-outline"
          :label="$t('common.delete')"
          @click="emit('delete', props.modelValue)"
        />
        <q-space />
        <q-btn flat :label="$t('common.cancel')" @click="emit('cancel')" />
        <q-btn
          color="primary"
          :label="$t('common.save')"
          :disable="!isValid"
          @click="onSave"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
